/*#
# Export and restore: the practices' backup

Practices, policies and guidance are meant to be impossible to destroy
(board #2479). The export writes them out as files a git repo can keep, and
a restore rebuilds them on a host from those files.

- **`registry/<id>.md`**: one file per entry, named by its id, which never
  changes, so a link to it never breaks. Front matter holds what the board
  knows (type, topics, mode, triggers, procedure, measure, evidence); the
  body is the entry's text.
- **`registry/README.md`**: the index, by type and topic.
- **`registry/events.jsonl`**: every event of every entry, in log order —
  the lossless part. Restore replays it.
- **`registry/manifest.json`**: each entry's number on the host it came
  from, and which projects opted in to which practices.

The export is for a public repo: it drops the host's provenance stamps,
redacts email addresses, and names every project not marked public as "a
private project" — in identities, tags, text and the manifest, so a private
repo that adopted a notice isn't named (board #2479). Attached files are
named, not copied.

A restore writes only the events the host doesn't hold, so running it twice
writes nothing the second time. Numbers are per host, so a tag such as
`chains-from:#12` is renumbered to what that entry is on the new host, and
a project's opt-in is restored by id.

```
virta export ../tosijs-coding-practices     # writes registry/
virta restore ../tosijs-coding-practices --dry
```
*/

/*{ "parent": "Reference", "order": 38 }*/

import type { EventDraft, Task, VirtaEvent } from './model'
import { isPublic } from './public'
import { actingFor } from './skills'
import { evidenceGrade, lineage, recordLine, TRIGGER_KEY } from './practices'
import { guide } from './guidance'
import type { VirtaStore } from './store'
import { ENTITY_TYPES, tagValues, TYPE_KEY } from './tags'

/** Where the export writes, under the directory it is given. */
export const REGISTRY_DIR = 'registry'

/** An address, which a public export must not carry. */
const EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g

/** What a project the export may not name becomes (board #2479). */
export const PRIVATE_PROJECT = 'a private project'

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * A redactor for one export: email addresses, and every project not marked
 * public — the board's projects, and any repo a writer acts for — named as a
 * whole word anywhere (an identity, a tag such as `adopted:<repo>@…`, a
 * comment, the manifest). A repo the board doesn't know is private.
 */
function scrubberFor(
  store: VirtaStore,
  events: readonly VirtaEvent[]
): <T>(value: T) => T {
  const open = new Set(store.projects.filter(isPublic).map((p) => p.name))
  const writers = events.flatMap((e) =>
    [e.by, (e as { via?: unknown }).via].filter(
      (x): x is string => typeof x === 'string'
    )
  )
  const hidden = [
    ...new Set([
      ...store.projects.map((p) => p.name),
      ...writers.map(actingFor),
    ]),
  ]
    .filter((name) => name && !open.has(name))
    // longest first, so a name never eats a longer one it starts
    .sort((a, b) => b.length - a.length)
  const names = hidden.length
    ? new RegExp(
        `(?<![\\w.-])(?:${hidden.map(escape).join('|')})(?![\\w-])`,
        'g'
      )
    : null
  const redact = (text: string) => {
    const t = text.replace(EMAIL, '<email>')
    return names ? t.replace(names, PRIVATE_PROJECT) : t
  }
  const scrub = <T>(value: T): T => {
    if (typeof value === 'string') return redact(value) as T
    if (Array.isArray(value)) return value.map(scrub) as T
    if (value && typeof value === 'object')
      return Object.fromEntries(
        // values only: a key is the event's shape, and a private project
        // named like a field (`text`) must not rename it (review 2026-10-16)
        Object.entries(value).map(([k, v]) => [k, scrub(v)])
      ) as T
    return value
  }
  return scrub
}

type Scrub = ReturnType<typeof scrubberFor>

/** An event as the export keeps it: no seq (the log's), no host stamp. */
const draftOf = (e: VirtaEvent, scrub: Scrub): EventDraft => {
  const { seq: _seq, attribution: _stamp, ...draft } = e
  return scrub(draft) as EventDraft
}

export interface ExportManifest {
  exportedAt: string
  entries: { id: string; num: number; type: string; title: string }[]
  /** project → the ids of the practices it opted in to */
  projects: Record<string, string[]>
}

export interface PracticesExport {
  /** path (under the target directory) → contents */
  files: Record<string, string>
  entries: number
  events: number
}

const isEntityType = (type: string) =>
  (ENTITY_TYPES as readonly string[]).includes(type)

/** The entries the export covers: every practice, policy and guidance, closed or archived too. */
function entriesOf(store: VirtaStore): Task[] {
  return store
    .query(`${TYPE_KEY}:any is:any archived:any`, null)
    .filter((t) => isEntityType(tagValues(t.tags, TYPE_KEY)[0] ?? ''))
    .sort((a, b) => a.num - b.num)
}

/** A project's `practices` setting as ids: `#12` → the id of #12. */
function practiceIds(store: VirtaStore, setting: string | undefined): string[] {
  return (setting ?? '')
    .split(/[\s,]+/)
    .filter(Boolean)
    .map((ref) => {
      const m = /^#(\d+)$/.exec(ref)
      return m ? store.byNum(Number(m[1]))?.id ?? '' : ref
    })
    .filter(Boolean)
}

const yamlValue = (v: unknown) => JSON.stringify(v)

/** One entry's file: front matter, then its text; a retired one says what replaced it. */
function entryFile(t: Task, store: VirtaStore, scrub: Scrub): string {
  const type = tagValues(t.tags, TYPE_KEY)[0]!
  const family = lineage(store, t)
  const front: [string, unknown][] = [
    ['id', t.id],
    ['number', t.num],
    ['type', type],
    ['title', t.title],
    ['status', t.closedAt ? 'closed' : 'open'],
    ['closed_as', tagValues(t.tags, 'closed')[0] ?? ''],
    ['topics', tagValues(t.tags, 'topic')],
    ['mode', tagValues(t.tags, 'mode')[0] ?? ''],
    ['triggers', tagValues(t.tags, TRIGGER_KEY)],
    ['tags', t.tags],
    [
      'procedure',
      t.procedure
        ? {
            when: t.procedure.when,
            set: t.procedure.set,
            version: t.procedure.version,
          }
        : null,
    ],
    [
      'measure',
      t.measure
        ? {
            measure: t.measure.measure,
            death: t.measure.death,
            version: t.measure.version,
          }
        : null,
    ],
    ['evidence', recordLine(t)],
    ['grade', evidenceGrade(t)],
    ['record', t.record],
    ['replaced_by', family.later.map((x) => x.id)],
    ['earlier', family.earlier.map((x) => x.id)],
    ['created', t.createdAt],
    ['updated', t.updatedAt],
  ]
  return scrub(
    [
      '---',
      ...front.map(([k, v]) => `${k}: ${yamlValue(v)}`),
      '---',
      '',
      `# ${t.title}`,
      '',
      // a tombstone: the file stays, so links to it land somewhere useful
      ...(family.later.length
        ? [
            `> **Retired.** Replaced by ${family.later
              .map((x) => `[${x.title}](${x.id}.md)`)
              .join(', ')}.`,
            '',
          ]
        : []),
      t.body.trim() || '_(no text yet)_',
      '',
    ].join('\n')
  )
}

/** The index: by type, then topic; closed entries apart. */
function indexFile(
  entries: Task[],
  exportedAt: string,
  store: VirtaStore,
  scrub: Scrub
): string {
  const lines = [
    '# The practices registry',
    '',
    `Exported from virta ${exportedAt.slice(
      0,
      10
    )}. Each entry's file is named by its id, which never changes. \`events.jsonl\` is the full history, the part a restore replays.`,
    '',
  ]
  const label = (t: Task) =>
    `- [${t.title}](${t.id}.md) — #${t.num}${
      tagValues(t.tags, 'mode')[0] ? `, ${tagValues(t.tags, 'mode')[0]}` : ''
    }, ${evidenceGrade(t)}`
  for (const type of ENTITY_TYPES) {
    const ofType = entries.filter(
      (t) => tagValues(t.tags, TYPE_KEY)[0] === type
    )
    if (!ofType.length) continue
    lines.push(`## ${type[0]!.toUpperCase()}${type.slice(1)}`, '')
    const open = ofType.filter((t) => !t.closedAt)
    const topics = [
      ...new Set(open.flatMap((t) => tagValues(t.tags, 'topic'))),
    ].sort()
    for (const topic of topics) {
      lines.push(`### ${topic}`, '')
      for (const t of open.filter((x) =>
        tagValues(x.tags, 'topic').includes(topic)
      ))
        lines.push(label(t))
      lines.push('')
    }
    const loose = open.filter((t) => !tagValues(t.tags, 'topic').length)
    if (loose.length) {
      if (topics.length) lines.push('### No topic', '')
      lines.push(...loose.map(label), '')
    }
    const closed = ofType.filter((t) => t.closedAt)
    if (closed.length)
      lines.push(
        '### Closed or retired',
        '',
        ...closed.map((t) => {
          const later = lineage(store, t).later
          return `${label(t)} (${tagValues(t.tags, 'closed')[0] ?? 'closed'}${
            later.length
              ? `; replaced by ${later
                  .map((x) => `[#${x.num}](${x.id}.md)`)
                  .join(', ')}`
              : ''
          })`
        }),
        ''
      )
  }
  return scrub(lines.join('\n'))
}

/**
 * The practices, policies and guidance as files (pure: reads the store).
 * Paths are under `registry/`.
 */
export function exportPractices(
  store: VirtaStore,
  exportedAt = new Date().toISOString()
): PracticesExport {
  const entries = entriesOf(store)
  const ids = new Set(entries.map((t) => t.id))
  const events = entries
    .flatMap((t) => store.events(t.id))
    .sort((a, b) => a.seq - b.seq)
  const scrub = scrubberFor(store, events)
  const projects: Record<string, string[]> = {}
  // a private project's opt-ins stay on the host: the manifest may not name it
  for (const p of store.projects.filter(isPublic)) {
    const listed = practiceIds(store, p.practices).filter((id) => ids.has(id))
    if (listed.length) projects[p.name] = listed
  }
  const manifest: ExportManifest = {
    exportedAt,
    entries: entries.map((t) => ({
      id: t.id,
      num: t.num,
      type: tagValues(t.tags, TYPE_KEY)[0]!,
      title: t.title,
    })),
    projects,
  }
  const dir = REGISTRY_DIR
  const files: Record<string, string> = {
    [`${dir}/README.md`]: indexFile(entries, exportedAt, store, scrub),
    [`${dir}/events.jsonl`]:
      events.map((e) => JSON.stringify(draftOf(e, scrub))).join('\n') +
      (events.length ? '\n' : ''),
    [`${dir}/manifest.json`]: JSON.stringify(scrub(manifest), null, 2) + '\n',
    // the way in, for an agent that knows nothing (board #2497)
    [`${dir}/llms.txt`]: scrub(guide(store).text),
  }
  for (const t of entries)
    files[`${dir}/${t.id}.md`] = entryFile(t, store, scrub)
  return { files, entries: entries.length, events: events.length }
}

/** A tag that names another task by number: `chains-from:#12`, `after:#40`. */
const NUMBERED_TAG = /^([\w-]+):#(\d+)$/

export interface RestorePlan {
  /** the events to write: the export's the host lacks, then the renumbering */
  drafts: EventDraft[]
  /** entries the export holds that the host doesn't (yet) */
  restored: number
  /** `#old → #new` for each tag renumbered */
  renumbered: string[]
  /** project opt-ins to add: project → the ids it lacks */
  projects: Record<string, string[]>
  /** tags naming a number the export doesn't hold: left as they are */
  unresolved: string[]
}

/** Parse the export's event log: one event per line. */
export function parseExportEvents(text: string): EventDraft[] {
  return text
    .split(/\r?\n/)
    .filter((l) => l.trim())
    .map((l) => JSON.parse(l) as EventDraft)
}

/**
 * What a restore writes (pure: reads the store, writes nothing). Events the
 * host already holds are skipped, so a second run plans nothing.
 * Renumbering is planned against the store as it will be once the events
 * are in: `renumber` runs after they land.
 */
export function planRestore(
  store: VirtaStore,
  events: EventDraft[]
): { drafts: EventDraft[]; restored: number } {
  const drafts = events.filter((e) => !store.holds(e.id))
  const created = drafts.filter((e) => e.kind === 'created').length
  return { drafts, restored: created }
}

/**
 * After the events are in: the tags that name an exported entry by its old
 * number, renumbered to what it is on this host, and the project opt-ins
 * the host lacks. Event ids are made from what they fix, so a second run
 * plans nothing.
 */
export function planRenumber(
  store: VirtaStore,
  manifest: ExportManifest
): Omit<RestorePlan, 'restored'> {
  const drafts: EventDraft[] = []
  const renumbered: string[] = []
  const unresolved: string[] = []
  const byOldNum = new Map(manifest.entries.map((e) => [e.num, e.id]))
  const at = new Date().toISOString()
  for (const entry of manifest.entries) {
    const task = store.get(entry.id)
    if (!task) continue
    for (const tag of task.tags) {
      const m = NUMBERED_TAG.exec(tag)
      if (!m) continue
      const target = byOldNum.get(Number(m[2]))
      if (!target) {
        // a number outside the export: correct only on the host it came from
        if (task.num !== entry.num) unresolved.push(`#${task.num} ${tag}`)
        continue
      }
      const now = store.get(target)?.num
      if (!now || now === Number(m[2])) continue
      const fixed = `${m[1]}:#${now}`
      const base = `restore:${task.id}:${tag}`
      if (store.holds(`${base}:tag`)) continue
      drafts.push(
        {
          id: `${base}:untag`,
          taskId: task.id,
          at,
          by: store.identity,
          kind: 'untagged',
          payload: { tag },
        } as EventDraft,
        {
          id: `${base}:tag`,
          taskId: task.id,
          at,
          by: store.identity,
          kind: 'tagged',
          payload: { tag: fixed },
        } as EventDraft
      )
      renumbered.push(`#${task.num} ${tag} → ${fixed}`)
    }
  }
  const projects: Record<string, string[]> = {}
  for (const [name, ids] of Object.entries(manifest.projects)) {
    const have = practiceIds(store, store.project(name).practices)
    const lacking = ids.filter((id) => store.get(id) && !have.includes(id))
    if (lacking.length) projects[name] = lacking
  }
  return { drafts, renumbered, projects, unresolved }
}

/**
 * Restore an export onto this host: the events it lacks, then the
 * renumbering and the project opt-ins. With `dry`, only the plan.
 */
export async function restorePractices(
  store: VirtaStore,
  input: { events: string; manifest: string; dry?: boolean }
): Promise<RestorePlan> {
  const events = parseExportEvents(input.events)
  const manifest = JSON.parse(input.manifest) as ExportManifest
  const restore = planRestore(store, events)
  if (input.dry) {
    // the renumbering is known only once the events are in: plan it on a copy
    const report = await store.transaction(
      async (scratch) => {
        await scratch.import(restore.drafts)
        return planRenumber(scratch, manifest)
      },
      { dry: true }
    )
    return {
      ...report.result,
      drafts: [...restore.drafts, ...report.result.drafts],
      restored: restore.restored,
    }
  }
  if (restore.drafts.length) await store.import(restore.drafts)
  const fix = planRenumber(store, manifest)
  if (fix.drafts.length) await store.import(fix.drafts)
  for (const [name, ids] of Object.entries(fix.projects)) {
    const have = practiceIds(store, store.project(name).practices)
    await store.setProject(name, { practices: [...have, ...ids].join(' ') })
  }
  return {
    ...fix,
    drafts: [...restore.drafts, ...fix.drafts],
    restored: restore.restored,
  }
}
